import { createSelector, createSlice } from "@reduxjs/toolkit";
import { getOrderDepthsAndLatestTradesAction, getTickersAction, getListTradeBaseOnPeriodAction, getAnnualClosePricesAction, getHistoricalSharesAction, getHistoricalPricesAction } from "../../actions";
import Instrument from "../../entity/Instrument";
import {instrumentSettingsIndexer } from "./settings/selectors";
import { sortOrderData, findMinStartingDate } from '../../utils'
import { setSettings } from "./settings/main";

/**
 * @typedef { import("..").RootState }  RootState
 * @typedef { import('../../..').IAnnualClosePrice } IAnnualClosePrice
 * @typedef { import('../../..').IInstrument } IInstrument
 * @typedef { import('../../..').IHistoricalPrices } IHistoricalPrices
 * @typedef { import("../../..").UnWrapRecord } UnWrapRecord
 */


const initialState = {
  historicalPrices: /** @type { Record<number, IHistoricalPrices>} */ ({}),
  getHistoricalPrices: {
    ids: /** @type { number[] }  */ ([]),
    loading: undefined,
    fetchError: null,
  },
  instruments: /** @type { Record<string | number, IInstrument>} */ ({}),
  getInstruments: {
    ids: [],
    loading: undefined,
    refreshing: undefined,
    fetchError: null,
    refreshError: null,
    loaded: false
  },
  annualClosePrices: /** @type { Record<string | number, IAnnualClosePrice>} */ ({}),
  getAnnualClosePrices: {
    ids: /** @type { number[] }  */ ([]) ,
    loading: undefined,
    loaded: false,
    refreshing: undefined,
    fetchError: null,
    refreshError: null,
  },
  listTradeBaseOnPeriod: [],
  getListTradeBaseOnPeriod: {
    ids: [],
    loading: undefined,
    refreshing: undefined,
    fetchError: null,
    refreshError: null,
  },
  getHistoricalShares: {
    loading: undefined,
    refreshing: undefined,
    fetchError: null,
    refreshError: null,
  },
  historicalShareList: [],
  selectedInstrumentId: /** @type { Record<number, number>} */ ({}), // { id: id }
  orderDepth: {},
  
  entryOrderDepth: {
    ids: [],
    rowUpdated: "",
    loading: undefined,
    refreshing: undefined,
    fetchError: null,
    refreshError: null,
  },
  tradeHistory: {},
  entryTradeHistory: {
    ids: [],
    loading: undefined,
    refreshing: undefined,
    fetchError: null,
    refreshError: null,
  },
  linkDownload: '',
  minStartingDateInstrument: '',
};
const tradeSlice = createSlice({
  name: "trade",
  initialState,
  reducers: {
    setSelectedInstrument: (state, { payload: id }) => {
      const clone = { ...state.selectedInstrumentId };
      if (id in clone) {
        delete clone[id];
      } else {
        clone[id] = id;
      }

      // find min startingDate in selectedInstrument
      const minStartingDateSelectedInstrument = findMinStartingDate(state.instruments, clone)
      state.minStartingDateInstrument = minStartingDateSelectedInstrument;

      state.selectedInstrumentId = clone;
    },
    saveLinkDownload: (state, { payload }) => {
      state.linkDownload = payload;
    },
   
  },
  extraReducers: builder => {
    builder.addCase(getTickersAction.pending, (state, action) => {
      state.getInstruments.loading = true;
    });
    builder.addCase(getTickersAction.fulfilled, (state, { payload }) => {
      const instrumentDatas = payload.data.instruments || [];
      state.instruments = instrumentDatas.reduce(
        (s, inst) => ({ ...s, [inst.instrumentId]: new Instrument(inst) }),
        {}
      );

      // find min startingDate in selectedInstrument
      const minStartingDateSelectedInstrument = findMinStartingDate(state.instruments, state.selectedInstrumentId)

      state.minStartingDateInstrument = minStartingDateSelectedInstrument;
      state.getInstruments.ids = instrumentDatas.map(inst => inst.instrumentId);
      state.getInstruments.loading = false;
      state.getInstruments.loaded = true;
      // when have one instrument, we active this instrument ticket
      if (instrumentDatas.length === 1) state.selectedInstrumentId[instrumentDatas[0].instrumentId] = instrumentDatas[0].instrumentId
    });
    builder.addCase(getTickersAction.rejected, (state, action) => {
      state.fetchError = action.error;
      state.getInstruments.loading = false;
    });
    builder.addCase(setSettings.type, (state, { payload }) => {
      let selected = payload.instruments.find(ins => !!ins.default) || payload.instruments[0];
      state.selectedInstrumentId = {
        ...state.selectedInstrumentId,
        [selected.id]: selected.id,
      };
    });

    builder.addCase(getOrderDepthsAndLatestTradesAction.pending, state => {
      state.entryOrderDepth.loading = true;
      state.entryTradeHistory.loading = true;
    });
    builder.addCase(getOrderDepthsAndLatestTradesAction.fulfilled, (state, { payload }) => {
      const { latestTrades, orderDepths } = payload.data || {};
      const getLatestTrades = latestTrades.length ? latestTrades : [];
      const getOrderDepths = orderDepths.marketDepth.length ? orderDepths.marketDepth : [];
      state.entryOrderDepth.rowUpdated = orderDepths.rowUpdated;
      state.entryTradeHistory.ids = getLatestTrades.map((l_, i) => i);
      state.tradeHistory = getLatestTrades.reduce(
        (s, lt, i) => ({
          ...s,
          [i]: lt,
        }),
        {}
      );
      state.entryOrderDepth.ids = getOrderDepths.map((_, i) => i);
      state.orderDepths = getOrderDepths.reduce(
        (s, od, i) => ({
          ...s,
          [i]: od,
        }),
        {}
      );
      state.entryOrderDepth.loading = false;
      state.entryTradeHistory.loading = false;
    });
    builder.addCase(getOrderDepthsAndLatestTradesAction.rejected, (state, { payload }) => {
      state.entryOrderDepth.fetchError = payload;
      state.entryTradeHistory.fetchError = payload;
      state.entryOrderDepth.loading = false;
      state.entryTradeHistory.loading = false;
    });
    builder.addCase(getListTradeBaseOnPeriodAction.pending, (state, action) => {
      state.getListTradeBaseOnPeriod.loading = true;
    });
    builder.addCase(getListTradeBaseOnPeriodAction.fulfilled, (state, { payload }) => {
      state.getListTradeBaseOnPeriod.loading = false; 
      const sharePricesOrder = sortOrderData(payload, state.instruments)
      state.listTradeBaseOnPeriod = sharePricesOrder
    });
    builder.addCase(getListTradeBaseOnPeriodAction.rejected, (state, action) => {
      state.fetchError = action.error;
      state.getListTradeBaseOnPeriod.loading = false;
    });
    builder.addCase(getAnnualClosePricesAction.pending, (state, action) => {
      state.getAnnualClosePrices.loading = true;
    });
    builder.addCase(getAnnualClosePricesAction.fulfilled, ( state, { payload }) => {
      state.getAnnualClosePrices.loaded = true;
      const annualClosePricesOrder = sortOrderData(payload.data.annualClosePrices, state.instruments)

      state.annualClosePrices = annualClosePricesOrder.reduce((acc, item) => {
        acc[item.instrumentId] = item;
        return acc;
      }, {})
      state.getAnnualClosePrices.ids = annualClosePricesOrder.map(item => item.instrumentId)
      state.getAnnualClosePrices.loading = false;
      state.getAnnualClosePrices.fetchError = false;
      state.getAnnualClosePrices.isAllLessTwoYearAnnualClosePrices = payload.isAllLessTwoYearAnnualClosePrices;
    });
    builder.addCase(getAnnualClosePricesAction.rejected, (state) => {
      state.getAnnualClosePrices.fetchError = true;
    });

    builder.addCase(getHistoricalSharesAction.pending, (state, action) => {
      state.getHistoricalShares.loading = true;
    });
    builder.addCase(getHistoricalSharesAction.fulfilled, (state, { payload }) => {
      state.getHistoricalShares.loading = false;
      const historicalSharesOrder = sortOrderData(payload, state.instruments)
      state.historicalShareList = historicalSharesOrder;
    });
    builder.addCase(getHistoricalSharesAction.rejected, (state, action) => {
      state.fetchError = action.error;
      state.getHistoricalShares.loading = false;
    });
    builder.addCase(getHistoricalPricesAction.pending, (state) => {
      state.getHistoricalPrices.loading = true;
    })
    builder.addCase(getHistoricalPricesAction.fulfilled, (state, { payload }) => {
      state.getHistoricalPrices.loading = false; 
      const getHistoricalPricesOrder = sortOrderData(payload.data.historicalPrices, state.instruments)

      state.getHistoricalPrices.ids = getHistoricalPricesOrder.map(item => item.instrumentId)
      state.historicalPrices = getHistoricalPricesOrder.reduce((acc, item) => {
        acc[item.instrumentId] = item;
        return acc;
      }, {})
    })
    builder.addCase(getHistoricalPricesAction.rejected, (state) => {
      state.getHistoricalPrices.fetchError = true;
    })
  },
});

export default tradeSlice;

export const tradeSelector = ( /** @type { RootState }  */state) => state.trade
export const instrumentsIndexerSelector = ( /** @type { RootState }  */state) => state.trade.instruments
export const loadedInstrumentSelector = ( /** @type { RootState }  */state) => state.trade.getInstruments.loaded

export const tradeHistorySelector = createSelector(
  tradeSelector,
  trade => ({
    tradeHistories: trade.entryTradeHistory.ids.map(id => trade.tradeHistory[id]),
    loading: trade.entryTradeHistory.loading,
    error: trade.entryTradeHistory.error,
  })
);

export const orderDepthSelector = createSelector(
  tradeSelector,
  trade => ({
    orderDepths: trade.entryOrderDepth.ids.map(id => trade.orderDepths[id]),
    loading: trade.entryOrderDepth.loading,
    error: trade.entryOrderDepth.error,
  })
);

export const instrumentSelector = createSelector(
  tradeSelector,
  trade => ({
    instruments: trade.getInstruments.ids.map(id => trade.instruments[id]),
    loading: trade.getInstruments.loading,
    error: trade.getInstruments.error,
  })
);

export const listTradeBaseOnPeriodSelector = createSelector(
  tradeSelector,
  trade => ({
    listTradeBaseOnPeriod: trade.listTradeBaseOnPeriod,
    loading: trade.getListTradeBaseOnPeriod.loading,
    error: trade.getListTradeBaseOnPeriod.error,
  })
);

export const historicalSharesSelector = createSelector(
  state => state.trade,
  trade => ({
    historicalShareList: trade.historicalShareList,
    loading: trade.getHistoricalShares.loading,
    error: trade.getHistoricalShares.error,
  })
);

export const linkDownloadSelector = createSelector(
  state => state.trade,
  trade => trade.linkDownload
);

export const minStartingDateInstrumentSelector = state => state.trade.minStartingDateInstrument

export const selectedInstrumentSelector = createSelector(tradeSelector, trade =>
  Object.keys(trade.selectedInstrumentId).reduce((acc, id) => {
    acc[id] = trade.instruments[id];
    return acc;
  }, {})
);
export const activeInstrumentSelector = (/** @type { RootState }  */state) => state.trade.selectedInstrumentId;
export const getAnnualClosePricesSelector = ( /** @type { RootState }  */state) => tradeSelector(state).getAnnualClosePrices
export const annualClosePricesSelector = createSelector(
  [( /** @type { RootState }  */state) => tradeSelector(state).annualClosePrices, getAnnualClosePricesSelector],
  (annualClosePrices, getAnnualClosePrices) => ({
    annualClosePrices: getAnnualClosePrices.ids.map(id => annualClosePrices[id]),
    loading: getAnnualClosePrices.loading,
    error: getAnnualClosePrices.fetchError,
    loaded: getAnnualClosePrices.loaded,
    isAllLessTwoYearAnnualClosePrices: getAnnualClosePrices.isAllLessTwoYearAnnualClosePrices,
  })
);

export const listHistoricalPrices = createSelector(
  [
    (/** @type { RootState }  */ state) => state.trade.getHistoricalPrices.ids,
    (/** @type { RootState }  */ state) => state.trade.historicalPrices,
  ],
  (ids, historicalPrices) => ids.map(id => historicalPrices[id])
);

export const getHistoricalPricesSelector = createSelector(
  [
    (/** @type { RootState }  */state) => state.trade.getHistoricalPrices, 
    (/** @type { RootState }  */state) => state.trade.historicalPrices,
    listHistoricalPrices
  ],
  (getHistoricalPrices, historicalPrices, list) => {
    // console.log('Opps!! some thing change => getHistoricalPricesSelector')
    return ({
      entries: historicalPrices,
      ids: getHistoricalPrices.ids,
      list, // we break down this list to another selector for optimize case loading fetchError property change frequently
      loading: getHistoricalPrices.loading,
      error: getHistoricalPrices.fetchError,
    })}
) ;

/**
 * instrument settings and instrument data (loaded by another api) depend together, we merge both to one to easy select and get data or custom field 
 * eg: we want select instrument name and custom field color one time, color is the custom field in setting, instrument name in SData response 
 * we need select both to get it.
 * 
 * @typedef { Record<number, { 
 * setting: import("../../..").UnWrapRecord<ReturnType<typeof instrumentSettingsIndexer>>, 
 * data: import("../../..").UnWrapRecord<ReturnType<typeof instrumentsIndexerSelector>>
 * }> } IInstrumentDataAndSettingSelectorReturn
 */
export const instrumentDataAndSettingSelector = createSelector([instrumentsIndexerSelector, instrumentSettingsIndexer], (data, settings) => {
  return Object.keys(settings).reduce((acc, key) => {
    acc[key] = {
      setting: settings[key],
      data: data[key]
    }
    return acc;
  }, /** @type { IInstrumentDataAndSettingSelectorReturn } */({}))
})

export const { setSelectedInstrument, saveLinkDownload,  } = tradeSlice.actions;
